/**
 * User Notice
 *
 * A popup meant for one user: rows in machina_user_notices with no seen_at.
 * Shows the oldest unseen notice on load; dismissing it stamps seen_at, so it
 * never shows again on any device. Same parchment look as the Town Crier.
 */

import { useEffect, useState } from 'react';
import { GouldCorner } from './Ornaments';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../stores/authStore';

interface Notice {
  id: string;
  title: string;
  body: string;
  link_url: string | null;
  link_label: string | null;
  sender: string | null;
}

const GOLD = '#d4af37';
const PARCHMENT = '#f4e4bc';
const PARCHMENT_DARK = '#d4c4a0';
const INK = '#2a1810';

export function UserNotice() {
  const userId = useAuthStore((s) => s.user?.id);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    void (async () => {
      const { data, error } = await supabase
        .from('machina_user_notices')
        .select('id, title, body, link_url, link_label, sender')
        .eq('user_id', userId)
        .is('seen_at', null)
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        console.warn('[UserNotice] load failed:', error.message);
        return;
      }
      if (data) {
        setNotice(data as Notice);
        setTimeout(() => setIsAnimating(true), 50);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  if (!notice) return null;

  const handleDismiss = () => {
    setIsAnimating(false);
    const id = notice.id;
    void supabase
      .from('machina_user_notices')
      .update({ seen_at: new Date().toISOString() })
      .eq('id', id)
      .then(({ error }) => {
        if (error) console.warn('[UserNotice] mark seen failed:', error.message);
      });
    setTimeout(() => setNotice(null), 300);
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Above the Town Crier, which can open at the same moment.
        zIndex: 3100,
        opacity: isAnimating ? 1 : 0,
        transition: 'opacity 0.3s ease-out',
      }}
    >
      <div
        role="dialog"
        aria-labelledby="user-notice-title"
        style={{
          position: 'relative',
          width: 480,
          maxWidth: '90vw',
          maxHeight: '80vh',
          overflowY: 'auto',
          background: `linear-gradient(180deg, ${PARCHMENT} 0%, ${PARCHMENT_DARK} 100%)`,
          borderRadius: 8,
          padding: 'var(--spacing-xl)',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 40px rgba(212, 175, 55, 0.2)',
          border: `3px solid ${GOLD}`,
          transform: isAnimating ? 'scale(1)' : 'scale(0.9)',
          transition: 'transform 0.3s ease-out',
          color: INK,
        }}
      >
        <GouldCorner size={32} color={GOLD} style={{ position: 'absolute', top: 8, left: 8 }} />
        <GouldCorner size={32} color={GOLD} style={{ position: 'absolute', top: 8, right: 8, transform: 'scaleX(-1)' }} />
        <GouldCorner size={32} color={GOLD} style={{ position: 'absolute', bottom: 8, left: 8, transform: 'scaleY(-1)' }} />
        <GouldCorner size={32} color={GOLD} style={{ position: 'absolute', bottom: 8, right: 8, transform: 'scale(-1)' }} />

        <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-lg)' }}>
          <div style={{ fontSize: 12, fontFamily: 'var(--font-display)', opacity: 0.6, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 4 }}>
            {notice.sender ? `A letter from ${notice.sender}` : 'A letter for you'}
          </div>
          <h2 id="user-notice-title" style={{ margin: 0, fontSize: 26, fontFamily: 'var(--font-display)', fontWeight: 400, letterSpacing: 1 }}>
            {notice.title}
          </h2>
        </div>

        <div style={{ height: 2, background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`, marginBottom: 'var(--spacing-lg)' }} />

        <div style={{ fontSize: 15, fontFamily: 'var(--font-body)', lineHeight: 1.55, whiteSpace: 'pre-line', marginBottom: 'var(--spacing-lg)' }}>
          {notice.body}
        </div>

        {notice.link_url && /^https?:\/\//.test(notice.link_url) && (
          <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-md)' }}>
            <a
              href={notice.link_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontFamily: 'var(--font-display)', fontSize: 15, color: '#8b6914', textDecoration: 'underline' }}
            >
              {notice.link_label || 'Open'}
            </a>
          </div>
        )}

        <div style={{ textAlign: 'center' }}>
          <button
            onClick={handleDismiss}
            style={{
              padding: 'var(--spacing-sm) var(--spacing-xl)',
              fontSize: 14,
              fontFamily: 'var(--font-display)',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 1,
              background: `linear-gradient(180deg, ${GOLD} 0%, #a08030 50%, #8b6914 100%)`,
              border: `2px solid ${GOLD}`,
              borderRadius: 4,
              color: INK,
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
            }}
          >
            Huzzah!
          </button>
        </div>
      </div>
    </div>
  );
}
